import React, { useState } from 'react'
import {
  Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, FormControlLabel, FormHelperText,
  Grid, InputLabel, MenuItem, Select, Switch, TextField
} from '@mui/material'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import { createPrinter, outcomeOf, PRINTER_REQUEST_FIELDS, updatePrinter } from '../../generic/qr/labelPrinting/labelPrintingApi'
import { DPI_OPTIONS, OUTCOME_TEXT, PRINT_METHOD_TEXT, textOf } from '../../generic/qr/labelPrinting/labelPrintingTexts'

const NEW_PRINTER = {
  name: '',
  host: '',
  port: '9100',
  model: '',
  dpi: '',
  labelWidthMm: '',
  labelHeightMm: '',
  printWidthMm: '',
  horizontalOffsetMm: '0',
  printMethod: '',
  active: true
}

const NUMERIC_FIELDS = ['port', 'dpi', 'labelWidthMm', 'labelHeightMm', 'printWidthMm', 'horizontalOffsetMm']

// E6: a printer without a configured print width uses the one it reports (Sun answers null).
const formOf = (printer) => PRINTER_REQUEST_FIELDS.reduce((form, field) => ({
  ...form,
  [field]: NUMERIC_FIELDS.includes(field) ? String(printer[field] ?? '') : printer[field]
}), {})

// API boundary (E6): an empty print width travels as null ("use the reported one"), an empty offset as 0.
const numberOf = (field, value) => {
  if (value !== '') return Number(value)
  if (field === 'printWidthMm') return null
  return field === 'horizontalOffsetMm' ? 0 : Number(value)
}

// LabelPrinterRequest: exactly the contract fields, numbers as numbers (S35).
const requestOf = (form) => PRINTER_REQUEST_FIELDS.reduce((request, field) => ({
  ...request,
  [field]: NUMERIC_FIELDS.includes(field) ? numberOf(field, form[field]) : form[field]
}), {})

// Historia 226 (S33-S38, CU1-CU2): create and edit form. Sun saves AND checks the printer before answering, so the
// wait can last the check's timeouts: the button says "Checking…" meanwhile. Sun's verdict on a field comes back as
// INVALID_PRINTER { field } or PRINTER_NAME_TAKEN, shown on that field with the form still open.
// E17 (S225): printers already at this address (host trimmed and case-insensitive, same port), apart from this one.
const printersAtSameAddress = (printers, form, ownId) => {
  const host = String(form.host).trim().toLowerCase()
  if (host === '') return []
  return printers.filter((other) => other.id !== ownId && String(other.host).trim().toLowerCase() === host && Number(other.port) === Number(form.port))
}

const PrinterFormDialog = ({ printer = {}, printers = [], onClose, onSaved }) => {
  const { t_ } = useSmartTranslation()
  const editing = Boolean(printer.id)
  const [form, setForm] = useState(editing ? formOf(printer) : NEW_PRINTER)
  const [fieldErrors, setFieldErrors] = useState({})
  const [generalError, setGeneralError] = useState('')
  const [saving, setSaving] = useState(false)
  const [sharedWith, setSharedWith] = useState([]) // names of the other printers at this address, while asking

  const change = (field) => (event) => {
    const value = field === 'active' ? event.target.checked : event.target.value
    setForm((current) => ({ ...current, [field]: value }))
  }

  // RN3 allows two printers at the same address; the user is only warned before saving (E17).
  const save = async (confirmed = false) => {
    if (saving) return
    if (!confirmed) {
      const others = printersAtSameAddress(printers, form, printer.id)
      if (others.length > 0) {
        setSharedWith(others.map((other) => other.name))
        return
      }
    }
    setSaving(true)
    setFieldErrors({})
    setGeneralError('')
    try {
      const saved = editing
        ? await updatePrinter(printer.id, requestOf(form))
        : await createPrinter(requestOf(form))
      onSaved(saved)
    } catch (error) {
      const outcome = outcomeOf(error)
      if (outcome === 'INVALID_PRINTER' && error.data.field) {
        setFieldErrors({ [error.data.field]: 'Invalid value' })
      } else if (outcome === 'INVALID_PRINTER') {
        setGeneralError('Invalid value')
      } else if (outcome === 'PRINTER_NAME_TAKEN') {
        setFieldErrors({ name: 'A printer with this name already exists' })
      } else {
        setGeneralError(textOf(OUTCOME_TEXT, outcome) || 'Unexpected error')
      }
      setSaving(false)
    }
  }

  const handleSave = () => save(false)
  const handleConfirmSave = () => {
    setSharedWith([])
    save(true)
  }
  const handleCancelSave = () => setSharedWith([])

  const textField = (field, label, type = 'text', help = '') => (
    <TextField
      id={`printer-${field}`}
      label={t_(label)}
      value={form[field]}
      onChange={change(field)}
      type={type}
      error={Boolean(fieldErrors[field])}
      helperText={fieldErrors[field] ? t_(fieldErrors[field]) : help}
      fullWidth
      size='small'
    />
  )

  const selectField = (field, label, options) => (
    <FormControl fullWidth size='small' error={Boolean(fieldErrors[field])}>
      <InputLabel id={`printer-${field}-label`}>{t_(label)}</InputLabel>
      <Select
        labelId={`printer-${field}-label`}
        label={t_(label)}
        value={form[field]}
        onChange={change(field)}
      >
        {options.map(({ value, text }) => <MenuItem key={value} value={value}>{text}</MenuItem>)}
      </Select>
      {fieldErrors[field] && <FormHelperText>{t_(fieldErrors[field])}</FormHelperText>}
    </FormControl>
  )

  return (
    <Dialog open onClose={saving ? undefined : onClose} maxWidth='sm' fullWidth>
      <DialogTitle>{t_(editing ? 'Edit printer' : 'New printer')}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ pt: 1 }}>
          <Grid item xs={12}>{textField('name', 'Name')}</Grid>
          <Grid item xs={8}>{textField('host', 'Host or IP address')}</Grid>
          <Grid item xs={4}>{textField('port', 'Port', 'number')}</Grid>
          <Grid item xs={6}>{textField('model', 'Model')}</Grid>
          <Grid item xs={6}>
            {selectField('dpi', 'Resolution (dpi)', DPI_OPTIONS.map((dpi) => ({ value: String(dpi), text: String(dpi) })))}
          </Grid>
          <Grid item xs={6}>{textField('labelWidthMm', 'Label width (mm)', 'number')}</Grid>
          <Grid item xs={6}>{textField('labelHeightMm', 'Label height (mm)', 'number')}</Grid>
          {/* E6: where the label sits on the print head; empty width = the one the printer reports when checked */}
          <Grid item xs={6}>
            {textField('printWidthMm', 'Print width (mm)', 'number', printer.reportedPrintWidthMm
              ? t_('The printer reports {{width}} mm', { width: printer.reportedPrintWidthMm })
              : '')}
          </Grid>
          <Grid item xs={6}>{textField('horizontalOffsetMm', 'Horizontal offset (mm)', 'number')}</Grid>
          <Grid item xs={8}>
            {selectField('printMethod', 'Print method', Object.keys(PRINT_METHOD_TEXT)
              .map((method) => ({ value: method, text: t_(PRINT_METHOD_TEXT[method]) })))}
          </Grid>
          <Grid item xs={4}>
            <FormControlLabel
              control={<Switch checked={form.active} onChange={change('active')} />}
              label={t_('Active')}
            />
          </Grid>
          {generalError && <Grid item xs={12}><Alert severity='error'>{t_(generalError)}</Alert></Grid>}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>{t_('Cancel')}</Button>
        <Button variant='contained' onClick={handleSave} disabled={saving}>
          {saving ? t_('Checking…') : t_('Save')}
        </Button>
      </DialogActions>
      <Dialog open={sharedWith.length > 0} onClose={handleCancelSave}>
        <DialogContent>
          {t_('There is already another printer at that address: {{names}}. Save anyway?', { names: sharedWith.join(', ') })}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelSave}>{t_('Cancel')}</Button>
          <Button variant='contained' onClick={handleConfirmSave}>{t_('Save')}</Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  )
}

export default PrinterFormDialog
